/**
 * createApiFacade — the canonical client response contract (RES-CLIENT-001 §8).
 *
 * ```js
 * const api = createApiFacade({ transport: createApiClient({ baseURL, getToken, … }) });
 *
 * const customers = await api.get('/customers');                    // ← the PAYLOAD
 * const { payload, pagination } = await api.withMeta.get('/customers');
 * const { body, filename }      = await api.download.get('/exports/a.csv');
 * ```
 *
 * | surface                | resolves                          | share of call sites |
 * | ---------------------- | --------------------------------- | ------------------- |
 * | `api.<verb>`           | the payload, verbatim             | ~98%                |
 * | `api.withMeta.<verb>`  | a frozen `ApiResult`              | ~2%                 |
 * | `api.download.<verb>`  | a frozen `DownloadResult`         | the blob surfaces   |
 * | `api.baseURL`          | `string`, read-only               | 6 URL builders      |
 *
 * ## 🔴 The disease this cures
 *
 * §4 measured THREE client contracts running side by side. `@puffin-core/api-client`
 * returns an axios response whose `.data` is the **envelope** (9 apps); pos-web's
 * `ApiClientManager` replaces `response.data` with the **payload** (1 app); two
 * bespoke pos-web clients register no response interceptor at all. So `.data.data`
 * is correct in port-web, correct for two pos-web clients, and wrong everywhere else
 * in pos-web — **decided by an import three files away**.
 *
 * The fleet's answer was **539 residual `.data.data` reads** and, in ELEVEN shared
 * `@puffin-core` packages, **13 runtime provenance sniffers**
 * (`response?.data?.data ?? response?.data ?? null` and its variants), because a
 * shared factory cannot know which contract its injected client obeys. `import-ui`
 * documents its compatibility layer in a comment. CLAUDE.md forbids exactly that —
 * *"NO BACKWARD COMPATIBILITY CODE"* — and **none of the eleven could stop until the
 * contract converged.** This facade is what lets all 13 be deleted.
 *
 * ## Why the payload is the DEFAULT and metadata is opt-in
 *
 * Measured: of 2,684 server success responses, 65 carry `pagination` and 152 carry
 * `meta` (2.4% / 5.7%); of 1,899 client wrapper methods, ~35 consume metadata. A
 * contract that always resolved a result object would tax 1,899 call sites to serve
 * ~35. A contract that resolved only the payload would silently drop a **one-time
 * plaintext API key** (`port-api/.../createApiKeysRouter.js:125` sends it as
 * `meta.apiKey`), the tenant context `DropdownService.js:198` reads, and the real
 * row totals `useCustomerData.js:124` needs. Both facts are true, so the channel
 * must exist and must not be the default.
 *
 * ## Scope note for the next reader
 *
 * `createApiClient` still returns the axios instance, and it is now only the facade's
 * TRANSPORT: every web client reaches the API through a facade built here (RES-CLIENT-001
 * Part 6, 2026-09-01, which deleted pos-web's `readPayload.js`). A test double of a facade
 * therefore resolves the PAYLOAD, never an envelope or an axios response (`createApiFacadeMock`
 * refuses one at runtime).
 *
 * @module @puffin-core/api-client/facade/createApiFacade
 */

import { CLIENT_TOO_OLD } from '@puffin-core/client-contract';

import { getPageBuildFreshnessMonitor } from '../buildFreshness.js';
import { readEnvelopeData } from '../envelope.js';
import { readHeader } from '../htmlResponseGuard.js';
import { toApiError } from './apiError.js';
import { createApiResult } from './apiResult.js';
import { createDownloadResult } from './downloadResult.js';
import { readContentDispositionFilename } from './contentDisposition.js';
import { getPageRequestTracker } from '../requestTracker.js';

/**
 * One facade surface: the six verbs (`buildSurface`). The default surface resolves the PAYLOAD, `withMeta` a frozen
 * ApiResult and `download` a frozen DownloadResult; each rejects with an `ApiError`. A request body is named `body`.
 *
 * @typedef {Object} ApiFacadeSurface
 * @property {(config: Object) => Promise<*>} request
 * @property {(url: string, config?: Object) => Promise<*>} get
 * @property {(url: string, config?: Object) => Promise<*>} delete
 * @property {(url: string, body?: *, config?: Object) => Promise<*>} post
 * @property {(url: string, body?: *, config?: Object) => Promise<*>} put
 * @property {(url: string, body?: *, config?: Object) => Promise<*>} patch
 */

/**
 * The frozen facade `createApiFacade` returns: the default surface's verbs, the `withMeta` and `download` surfaces, and
 * the `baseURL` download-URL builders read. Named at the package root (`import('@puffin-core/api-client').ApiFacade`)
 * for every client module that takes one injected (JSDOC-PROP-CONTRACT-001).
 *
 * @typedef {Readonly<ApiFacadeSurface & {withMeta: Readonly<ApiFacadeSurface>, download: Readonly<ApiFacadeSurface>,
 *   baseURL: string}>} ApiFacade
 */

/**
 * The default answer to a `CLIENT_TOO_OLD` refusal: the page's own freshness monitor makes the reload REQUIRED.
 * Resolved at the refusal, so a facade that is never refused never creates the monitor.
 */
function reportClientTooOldToPage() {
  getPageBuildFreshnessMonitor().requireReload();
}

/**
 * The one status with no body to read.
 *
 * `Q-RC-4` ruled that a 204 resolves `null` rather than converting all 54 emitting
 * endpoints to `sendSuccess(res, null)`: 204 is the correct HTTP answer for a
 * delete, and forcing a body to avoid one client branch is the tail wagging the dog.
 * The carve-out is EXACTLY 204 — a carve-out that crept up to "2xx with a smallish
 * body" is how a guard stops guarding.
 */
const NO_CONTENT_STATUS = 204;

/**
 * The default surface: the envelope's payload, verbatim.
 *
 * 🔴 `readEnvelopeData` **THROWS** on a non-canonical body rather than degrading to
 * a confident empty result, and that asymmetry with the error reader is the entire
 * point (`envelope.js:18-36`). Every hand-rolled reader in the fleet degraded —
 * `result?.data || []` and eight sibling variants in one planner-web feature — so an
 * unreadable body rendered as *"this tenant has no holidays"*: a confident zero
 * indistinguishable from the truth. Live proof: `@puffin-core/support-tickets`'
 * router ships `{rows, total}` with NO envelope while **mounted in 8 of 9 APIs**.
 *
 * **Do not "fix" this to a fallback.** Every consumer already has a `.catch` and an
 * honest error state; throwing routes an unreadable body there.
 *
 * @param {{status: number, data: *}} response - The transport response.
 * @returns {*} The payload, or null on a 204.
 */
function readPayload(response) {
  return response.status === NO_CONTENT_STATUS ? null : readEnvelopeData(response.data);
}

/**
 * The metadata surface: a frozen `ApiResult`.
 *
 * `pagination` and `meta` are read from the ENVELOPE, beside `data` — never from
 * inside it, and never normalized. Lane N9 is converging the wire onto
 * `{limit, offset, total, nextCursor, hasMore}`; this reader deliberately does not
 * re-assert that shape (§5.3(a)), so the two cannot disagree.
 *
 * @param {{status: number, data: *, headers: *}} response - The transport response.
 * @returns {Readonly<Object>} A frozen ApiResult.
 */
function readApiResult(response) {
  const { status, headers } = response;
  const envelope = status === NO_CONTENT_STATUS ? null : response.data;

  return createApiResult({
    // Evaluated first, so a non-canonical body throws before any member is built.
    payload: envelope === null ? null : readEnvelopeData(envelope),
    pagination: envelope?.pagination ?? null,
    meta: envelope?.meta ?? null,
    status,
    headers,
    // RES-ENV-001 puts the success-side correlation id on the HEADER, not in the
    // body (`response-envelope.md:78-86`). §5.2 measured ZERO production consumers
    // reading it, because no client exposes response headers at all — this is the
    // first surface that makes the documented mechanism reachable.
    requestId: readHeader(headers, 'x-request-id'),
  });
}

/**
 * The download surface: a frozen `DownloadResult`, with the envelope reader
 * deliberately absent.
 *
 * @param {{status: number, data: *, headers: *}} response - The transport response.
 * @returns {Readonly<Object>} A frozen DownloadResult.
 */
function readDownload(response) {
  const { status, headers } = response;

  return createDownloadResult({
    body: response.data,
    contentType: readHeader(headers, 'content-type'),
    filename: readContentDispositionFilename(readHeader(headers, 'content-disposition')),
    headers,
    status,
  });
}

/**
 * Build one surface's six verbs over a shared dispatcher.
 *
 * `method` and `url` are applied AFTER the caller's config so a config object can
 * never silently retarget a positional call — `api.get('/customers', {url: '/admin/keys'})`
 * stays a GET of `/customers`. A config that could override them would make every
 * call site's first argument advisory.
 *
 * The request body is named **`body`** on this surface and only becomes axios's
 * `data` key on the way out (§8.5 mechanism 2: the token `data` appears nowhere in
 * the contract, so no call site learns it from us).
 *
 * @param {Function} dispatch - `(config, interpret) => Promise<*>`.
 * @param {Function} interpret - Maps a transport response to this surface's value.
 * @param {Object} [defaults] - Per-surface request-config defaults, caller-overridable.
 * @returns {ApiFacadeSurface} The six verbs.
 */
function buildSurface(dispatch, interpret, defaults) {
  const send = (config) => dispatch(config, interpret);

  return {
    request: (config) => send({ ...defaults, ...config }),
    get: (url, config) => send({ ...defaults, ...config, method: 'get', url }),
    delete: (url, config) => send({ ...defaults, ...config, method: 'delete', url }),
    post: (url, body, config) => send({ ...defaults, ...config, method: 'post', url, data: body }),
    put: (url, body, config) => send({ ...defaults, ...config, method: 'put', url, data: body }),
    patch: (url, body, config) =>
      send({ ...defaults, ...config, method: 'patch', url, data: body }),
  };
}

/**
 * Issue one request and map its outcome onto the contract.
 *
 * The `interpret` call sits OUTSIDE the try/catch on purpose: an `EnvelopeShapeError`
 * is not a failure the server reported, and flattening it into an `ApiError` with a
 * fabricated status would hide a contract breach inside the ordinary error path,
 * which is where nobody looks.
 *
 * @param {{request: Function}} transport - The wrapped client.
 * @param {Object} config - The request config.
 * @param {Function} interpret - The surface's response reader.
 * @param {Object} hooks - What a safe reload needs to know (WEB-APP-VERSION-REFRESH-001).
 * @param {{begin: Function}} hooks.requestTracker - Counts a person's request until it settles.
 * @param {Function} hooks.onClientTooOld - Told of a 426 `CLIENT_TOO_OLD` refusal.
 * @returns {Promise<*>}
 */
async function send(transport, config, interpret, { requestTracker, onClientTooOld }) {
  // WEB-APP-VERSION-REFRESH-001: a request a person is waiting on holds a reload back until it settles; a background
  // poll never does.
  const end = config.background === true ? null : requestTracker.begin();
  let response;
  try {
    response = await transport.request(config);
  } catch (cause) {
    const error = toApiError(cause);
    // A server refused this build as too old: the page learns at once that its reload is required.
    if (error.errorCode === CLIENT_TOO_OLD.code) onClientTooOld(error);
    throw error;
  } finally {
    end?.();
  }
  return interpret(response);
}

/**
 * Create the frozen facade.
 *
 * @param {Object} [options]
 * @param {{request: Function}} options.transport - A `createApiClient` instance. It
 *   already carries the ABG-001 pre-flight, the HTML-body guard, the abort
 *   convergence and the 401-refresh ladder, and it rejects the `NormalizedError`
 *   shape — ONE provenance, deliberately (see `toApiError`).
 * @param {string} [options.baseURL] - Overrides the transport's own; exposed as
 *   `api.baseURL` for the download-URL builders.
 * @param {Object} [options.requestTracker] - Counts the requests a person is waiting on, so a reload waits for them
 *   (`createRequestTracker`); defaults to the page's (`getPageRequestTracker`). A `{ background: true }` request is
 *   never counted.
 * @param {Function} [options.onClientTooOld] - Told when a server refuses this client as too old (426
 *   `CLIENT_TOO_OLD`); defaults to making the page's reload REQUIRED (`getPageBuildFreshnessMonitor`).
 * @returns {ApiFacade} The frozen facade. It is NOT an axios instance, and
 *   the axios instance never escapes through it.
 * @throws {TypeError} When no usable transport is supplied.
 */
export function createApiFacade({
  transport,
  baseURL,
  requestTracker = getPageRequestTracker(),
  onClientTooOld = reportClientTooOldToPage,
} = {}) {
  if (!transport || typeof transport.request !== 'function') {
    // A facade over nothing would construct fine and fail on its first call with
    // "transport.request is not a function" — a stack trace pointing at the wrong
    // file. Same reasoning as `reportHtmlResponsesToAnalytics`'s guard
    // (`htmlResponseGuard.js:205-211`): a silent no-op is the worst outcome.
    throw new TypeError(
      'createApiFacade: `transport` must be a createApiClient instance exposing request(config).',
    );
  }

  const dispatch = (config, interpret) =>
    send(transport, config, interpret, { requestTracker, onClientTooOld });

  return Object.freeze({
    ...buildSurface(dispatch, readPayload),
    withMeta: Object.freeze(buildSurface(dispatch, readApiResult)),
    download: Object.freeze(buildSurface(dispatch, readDownload, { responseType: 'blob' })),
    // `?? ''` rather than `?? undefined`: a relative-URL client is legitimate, and
    // `undefined` would stringify into "undefined/exports/x.csv" at a URL builder.
    baseURL: baseURL ?? transport.defaults?.baseURL ?? '',
  });
}
